import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Palette, Loader2, RotateCcw, Check, Swords, Plus } from 'lucide-react';
import { api, parseApiError, type VetoModeKey } from '../lib/api';
import { AssetUploader } from '../components/AssetUploader';
import { AssetGrid } from '../components/AssetGrid';
import { Card } from '../components/Card';
import { SaveBar } from '../components/SaveBar';
import { PageSpinner } from '../components/Spinner';
import { PageWrapper } from '../components/PageWrapper';
import { orgSetupReturnPath } from '../lib/setupJump';

// Mirrors shared/orgVetoConfig.ts's map-name validator exactly: trimmed,
// 1–24 chars, first char alnum, rest may include space/period/apostrophe/
// hyphen. Client-side check is a UX nicety only — the backend re-validates
// on PATCH regardless (never trust the client).
const MAP_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 .'-]*$/;
const MAP_NAME_MAX_LEN = 24;
const MIN_MAPS_PER_MODE = 2;
const MAX_MAPS_PER_MODE = 10;

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const PRESET_ACCENTS = [
  '#ff731b', // forge — GG Anvil default
  '#3b82f6', // blue
  '#10b981', // emerald
  '#a855f7', // violet
  '#ef4444', // red
  '#f59e0b', // amber
  '#06b6d4', // cyan
  '#ec4899', // pink
];

export function OrgBranding() {
  const { orgId } = useParams<{ orgId: string }>();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  // Where the veto-config save below returns the user to — the OrgPanel
  // "Branding & veto" link (the only place this page is reached from) always
  // passes `?from=`; a direct/bookmarked visit with no history falls back to
  // the same Setup-org-tab path rather than a blind navigate(-1).
  const vetoReturnTo = searchParams.get('from') || orgSetupReturnPath();

  const { data: orgAssets, isLoading } = useQuery({
    queryKey: ['assets', 'org', orgId],
    queryFn: () => api.listAssets('org', orgId!),
    enabled: !!orgId,
  });

  const { data: config } = useQuery({
    queryKey: ['assetConfig', orgId],
    queryFn: () => api.getOrgAssetConfig(orgId!),
    enabled: !!orgId,
  });

  const { data: branding } = useQuery({
    queryKey: ['orgBranding', orgId],
    queryFn: () => api.getOrgBranding(orgId!),
    enabled: !!orgId,
  });

  const deleteMut = useMutation({
    mutationFn: (assetId: string) => api.deleteAsset(assetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets', 'org', orgId] });
      qc.invalidateQueries({ queryKey: ['assetConfig', orgId] });
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['assets', 'org', orgId] });
    qc.invalidateQueries({ queryKey: ['assetConfig', orgId] });
  };

  if (!orgId) return null;

  return (
    <PageWrapper maxWidth="4xl" spacing="lg">
      <div>
        <h1 className="font-display text-2xl font-800 uppercase tracking-wide text-white">Org Branding</h1>
        <p className="mt-1 text-sm text-iron-500">Accent colour, logos, and banners for this org.</p>
      </div>

      {/* Accent colour picker — white-label hook */}
      <AccentColorPicker
        orgId={orgId}
        currentColor={branding?.accentColor ?? null}
        onSaved={() => qc.invalidateQueries({ queryKey: ['orgBranding', orgId] })}
      />

      {/* GG Veto defaults — org-configurable series length + map pools */}
      <VetoConfigCard orgId={orgId} returnTo={vetoReturnTo} />

      {/* Storage */}
      {config && (
        <div className="flex items-center gap-4 text-xs text-iron-500">
          <span>{(config.storageUsed / 1024 / 1024).toFixed(1)}MB used</span>
          <div className="flex-1 h-1.5 rounded-full bg-iron-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-accent-500/60"
              style={{ width: `${Math.min(100, (config.storageUsed / config.storageCap) * 100)}%` }}
            />
          </div>
          <span>{(config.storageCap / 1024 / 1024).toFixed(0)}MB limit</span>
        </div>
      )}

      {/* Upload sections */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <AssetUploader
            ownerType="org"
            ownerId={orgId}
            assetType="logo"
            label="Organisation Logo"
            onUploaded={invalidate}
          />
        </Card>
        <Card>
          <AssetUploader
            ownerType="org"
            ownerId={orgId}
            assetType="banner"
            label="Organisation Banner"
            onUploaded={invalidate}
          />
        </Card>
      </div>

      {/* Asset grid */}
      <Card>
        <h2 className="mb-4 font-display text-sm font-800 uppercase tracking-wider text-iron-400">All Org Assets</h2>
        {isLoading ? <PageSpinner /> : (
          <AssetGrid
            assets={orgAssets?.assets || []}
            onDelete={id => deleteMut.mutate(id)}
          />
        )}
      </Card>
    </PageWrapper>
  );
}

function AccentColorPicker({
  orgId, currentColor, onSaved,
}: {
  orgId: string; currentColor: string | null; onSaved: () => void;
}) {
  const [draft, setDraft] = useState<string>(currentColor ?? '#ff731b');
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { if (currentColor) setDraft(currentColor); }, [currentColor]);

  const saveMut = useMutation({
    mutationFn: (color: string | null) => api.updateOrgBranding(orgId, color),
    onSuccess: () => {
      setInfo('Saved. Reload the page to see the accent everywhere.');
      setError('');
      onSaved();
    },
    onError: (e: Error) => { setError(parseApiError(e).message); setInfo(''); },
  });

  const valid = HEX_RE.test(draft);
  const dirty = draft.toLowerCase() !== (currentColor ?? '#ff731b').toLowerCase();

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <Palette className="h-4 w-4 text-accent-400" />
        <h2 className="font-display text-sm font-black uppercase tracking-wider text-accent-300">Accent colour</h2>
      </div>
      <p className="mb-3 text-xs text-iron-400">
        Sets the brand accent for this org's view of the portal. Stays orange (the GG Anvil house colour) if cleared.
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="color"
          value={valid ? draft : '#ff731b'}
          onChange={e => setDraft(e.target.value)}
          className="h-9 w-12 cursor-pointer border border-iron-700 bg-iron-900"
        />
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="#ff731b"
          className="w-28 border border-iron-700 bg-iron-950 px-2 py-1.5 font-mono text-xs text-white focus:border-iron-500 focus:outline-none"
        />
        <button
          disabled={!valid || !dirty || saveMut.isPending}
          onClick={() => saveMut.mutate(draft)}
          className="inline-flex items-center gap-1 border border-emerald-700/50 bg-emerald-900/30 hover:bg-emerald-900/60 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-200 disabled:opacity-50"
        >
          {saveMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" strokeWidth={2.5} />}
          Save
        </button>
        {currentColor && (
          <button
            onClick={() => saveMut.mutate(null)}
            disabled={saveMut.isPending}
            className="inline-flex items-center gap-1 border border-iron-700 hover:border-iron-500 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300"
          >
            <RotateCcw className="h-3 w-3" strokeWidth={2.5} />
            Reset to default
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {PRESET_ACCENTS.map(c => (
          <button
            key={c}
            onClick={() => setDraft(c)}
            title={c}
            className="h-7 w-7 border border-iron-700 hover:scale-110 transition-transform"
            style={{ backgroundColor: c }}
          />
        ))}
      </div>

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
      {info && !error && <p className="mt-2 text-xs text-emerald-300">{info}</p>}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VetoConfigCard — org-configurable /ggveto defaults: series length (Bo3/Bo5)
// + per-mode map pools. Purely additive on the backend (GET/PATCH
// /orgs/:orgId/veto-config) — no config here means /ggveto keeps behaving
// exactly as it always has (preset 'bo3-standard', CDL 2026 pools).
// ─────────────────────────────────────────────────────────────────────────────

function isValidMapNameClient(raw: string): boolean {
  const s = raw.trim();
  return s.length >= 1 && s.length <= MAP_NAME_MAX_LEN && MAP_NAME_RE.test(s);
}

function VetoConfigCard({ orgId, returnTo }: { orgId: string; returnTo: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['orgVetoConfig', orgId],
    queryFn: () => api.getOrgVetoConfig(orgId),
    enabled: !!orgId,
  });

  const [bestOf, setBestOf] = useState<3 | 5>(3);
  const [pools, setPools] = useState<Record<VetoModeKey, string[]>>({ HARDPOINT: [], SND: [], OVERLOAD: [] });
  const [drafts, setDrafts] = useState<Record<VetoModeKey, string>>({ HARDPOINT: '', SND: '', OVERLOAD: '' });
  const [mapErrors, setMapErrors] = useState<Record<VetoModeKey, string>>({ HARDPOINT: '', SND: '', OVERLOAD: '' });
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [initialized, setInitialized] = useState(false);
  // Dirty-tracking baseline (also the Discard target) — the bestOf/pools
  // values as last loaded or last saved. No parallel state invented beyond
  // this one snapshot, same idiom as the public-page editors' SaveBar wiring.
  const [initialSnapshot, setInitialSnapshot] = useState<{ bestOf: 3 | 5; pools: Record<VetoModeKey, string[]> } | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed local draft state once from the fetched config (or the league
  // defaults, when nothing's configured yet) — only ever on first load, so
  // in-progress edits are never clobbered by a background refetch.
  useEffect(() => {
    if (!data || initialized) return;
    const cfg = data.vetoConfig;
    const seededBestOf = cfg?.bestOf ?? data.defaults.bestOf;
    const seeded: Record<VetoModeKey, string[]> = { HARDPOINT: [], SND: [], OVERLOAD: [] };
    for (const mode of data.defaults.modes) {
      seeded[mode.key] = cfg?.pools?.[mode.key] ?? [...mode.maps];
    }
    setBestOf(seededBestOf);
    setPools(seeded);
    setInitialSnapshot({ bestOf: seededBestOf, pools: seeded });
    setInitialized(true);
  }, [data, initialized]);

  const usingLeagueDefaults = !data?.vetoConfig;
  const dirty = !!initialSnapshot && (
    bestOf !== initialSnapshot.bestOf || JSON.stringify(pools) !== JSON.stringify(initialSnapshot.pools)
  );

  const discardEdits = () => {
    if (!initialSnapshot) return;
    setBestOf(initialSnapshot.bestOf);
    setPools(initialSnapshot.pools);
    setMapErrors({ HARDPOINT: '', SND: '', OVERLOAD: '' });
    setDrafts({ HARDPOINT: '', SND: '', OVERLOAD: '' });
  };

  const saveMut = useMutation({
    mutationFn: () => api.updateOrgVetoConfig(orgId, { bestOf, pools }),
    onSuccess: () => {
      setInfo('Saved — this is what /ggveto will use for this org\'s server.');
      setError('');
      setInitialSnapshot({ bestOf, pools });
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['orgVetoConfig', orgId] });
      // Brief "Saved" confirmation, then hand the user back to the Setup
      // screen this page was opened FROM — don't strand them here after a
      // save (Lisa/coordinator, 2026-07-19).
      window.setTimeout(() => navigate(returnTo), 900);
    },
    onError: (e: Error) => { setError(parseApiError(e).message); setInfo(''); },
  });

  const resetMut = useMutation({
    mutationFn: () => api.updateOrgVetoConfig(orgId, { bestOf: null, pools: null }),
    onSuccess: () => {
      setInfo('Reset — /ggveto now uses the league defaults again.');
      setError('');
      setInitialized(false); // re-seed from the (now-cleared) config next fetch
      qc.invalidateQueries({ queryKey: ['orgVetoConfig', orgId] });
    },
    onError: (e: Error) => { setError(parseApiError(e).message); setInfo(''); },
  });

  const toggleMap = (mode: VetoModeKey, map: string) => {
    setPools(prev => {
      const has = prev[mode].some(m => m.toLowerCase() === map.toLowerCase());
      return { ...prev, [mode]: has ? prev[mode].filter(m => m.toLowerCase() !== map.toLowerCase()) : [...prev[mode], map] };
    });
  };

  const addCustomMap = (mode: VetoModeKey) => {
    const raw = drafts[mode].trim();
    if (!raw) return;
    if (!isValidMapNameClient(raw)) {
      setMapErrors(prev => ({ ...prev, [mode]: `1–${MAP_NAME_MAX_LEN} chars — letters, numbers, spaces, ' . - only` }));
      return;
    }
    if (pools[mode].some(m => m.toLowerCase() === raw.toLowerCase())) {
      setMapErrors(prev => ({ ...prev, [mode]: 'Already in this mode\'s pool' }));
      return;
    }
    if (pools[mode].length >= MAX_MAPS_PER_MODE) {
      setMapErrors(prev => ({ ...prev, [mode]: `Max ${MAX_MAPS_PER_MODE} maps per mode` }));
      return;
    }
    setPools(prev => ({ ...prev, [mode]: [...prev[mode], raw] }));
    setDrafts(prev => ({ ...prev, [mode]: '' }));
    setMapErrors(prev => ({ ...prev, [mode]: '' }));
  };

  const anyModeTooSmall = data?.defaults.modes.some(m => pools[m.key].length < MIN_MAPS_PER_MODE) ?? true;

  if (isLoading || !data) {
    return (
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Swords className="h-4 w-4 text-accent-400" />
          <h2 className="font-display text-sm font-black uppercase tracking-wider text-accent-300">GG Veto defaults</h2>
        </div>
        <PageSpinner />
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-1 flex items-center gap-2">
        <Swords className="h-4 w-4 text-accent-400" />
        <h2 className="font-display text-sm font-black uppercase tracking-wider text-accent-300">GG Veto defaults</h2>
      </div>
      <p className="mb-1 text-xs text-iron-400">
        What the Discord <span className="font-mono">/ggveto</span> command uses for this org's server — series length and each mode's map pool.
      </p>
      <p className="mb-4 text-[11px] text-iron-500">
        When a competition stage is live, that stage's own series format takes over automatically — series length here is
        only the fallback for when no stage is live. Map choices always apply.
      </p>

      {usingLeagueDefaults && (
        <p className="mb-4 text-[10px] font-bold uppercase tracking-wider text-iron-500">Using league defaults</p>
      )}

      {/* Series length fallback */}
      <div className="mb-5">
        <label className="mb-1.5 block font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-iron-500">
          Series length (fallback)
        </label>
        <div className="flex gap-2">
          {([3, 5] as const).map(n => (
            <button
              key={n}
              type="button"
              onClick={() => setBestOf(n)}
              className={`border px-4 py-1.5 font-display text-[11px] font-black uppercase tracking-wider ${
                bestOf === n
                  ? 'border-accent-500 bg-accent-500/10 text-accent-300'
                  : 'border-iron-700 text-iron-400 hover:border-iron-500'
              }`}
            >
              Bo{n}
            </button>
          ))}
        </div>
      </div>

      {/* Per-mode map pools */}
      <div className="space-y-5">
        {data.defaults.modes.map(mode => {
          const selected = pools[mode.key] ?? [];
          const tooSmall = selected.length < MIN_MAPS_PER_MODE;
          return (
            <div key={mode.key}>
              <div className="mb-1.5 flex items-baseline justify-between">
                <label className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-iron-500">
                  {mode.label}
                </label>
                <span className={`font-mono text-[10px] ${tooSmall ? 'text-red-400' : 'text-iron-500'}`}>
                  {selected.length} / {MAX_MAPS_PER_MODE} {tooSmall && `(min ${MIN_MAPS_PER_MODE})`}
                </span>
              </div>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {mode.maps.map(map => {
                  const isSelected = selected.some(m => m.toLowerCase() === map.toLowerCase());
                  return (
                    <button
                      key={map}
                      type="button"
                      onClick={() => toggleMap(mode.key, map)}
                      className={`border px-2.5 py-1 font-mono text-[11px] ${
                        isSelected
                          ? 'border-accent-500/60 bg-accent-500/10 text-accent-200'
                          : 'border-iron-700 text-iron-500 hover:border-iron-500'
                      }`}
                    >
                      {map}
                    </button>
                  );
                })}
                {/* Custom (non-league) maps currently in the pool render as
                    the same toggle chip — clicking removes them. */}
                {selected.filter(m => !mode.maps.some(lm => lm.toLowerCase() === m.toLowerCase())).map(customMap => (
                  <button
                    key={customMap}
                    type="button"
                    onClick={() => toggleMap(mode.key, customMap)}
                    className="border border-accent-500/60 bg-accent-500/10 px-2.5 py-1 font-mono text-[11px] text-accent-200"
                    title="Custom map — click to remove"
                  >
                    {customMap}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={drafts[mode.key]}
                  onChange={e => setDrafts(prev => ({ ...prev, [mode.key]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomMap(mode.key); } }}
                  placeholder="Add a custom map name"
                  maxLength={MAP_NAME_MAX_LEN}
                  className="w-48 border border-iron-700 bg-iron-950 px-2 py-1 font-mono text-[11px] text-white focus:border-iron-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => addCustomMap(mode.key)}
                  disabled={selected.length >= MAX_MAPS_PER_MODE}
                  className="inline-flex items-center gap-1 border border-iron-700 hover:border-iron-500 px-2 py-1 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 disabled:opacity-40"
                >
                  <Plus className="h-3 w-3" strokeWidth={2.5} />
                  Add
                </button>
              </div>
              {mapErrors[mode.key] && <p className="mt-1 text-[10px] text-red-400">{mapErrors[mode.key]}</p>}
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          onClick={() => resetMut.mutate()}
          disabled={resetMut.isPending || usingLeagueDefaults}
          className="inline-flex items-center gap-1 border border-iron-700 hover:border-iron-500 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 disabled:opacity-50"
        >
          <RotateCcw className="h-3 w-3" strokeWidth={2.5} />
          Reset to league defaults
        </button>
      </div>

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
      {info && !error && <p className="mt-2 text-xs text-emerald-300">{info}</p>}

      {/* Saving navigates back to Setup (the "Branding & veto" link's origin)
          shortly after the brief "Saved" confirmation — see saveMut above. */}
      <SaveBar
        className="mt-4"
        dirty={dirty}
        saving={saveMut.isPending}
        saved={saved}
        disabled={anyModeTooSmall}
        onSave={() => saveMut.mutate()}
        onDiscard={discardEdits}
      />
    </Card>
  );
}
